import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import ErrorCard from '../Components/ErrorCard';
import useUpdateNIS from '../Hooks/NisHooks/useUpdateNIS';
import useIAList from '../Hooks/NisHooks/useIAList';
import useISList from '../Hooks/NisHooks/useISList';
import MunicipalitySelector from '../Components/Municipality';
import MailingAddressSelector from '../Components/MailingAddressSelector';
import adminBg from '../Assets/bg/adminbg.jpg';


// Error Boundary Component
class ErrorBoundary extends React.Component {
state = { hasError: false, error: null };

static getDerivedStateFromError(error) {
  return { hasError: true, error };
}

render() {
  if (this.state.hasError) {
    return (
      <ErrorCard
        errorMessage={`Rendering error: ${this.state.error?.message || 'Unknown error'}`}
        onClose={this.props.onClose}
      />
    );
  }
  return this.props.children;
}
}


const NISEdit = ({ selectedItem, onClose, darkMode }) => {
  const { updateNIS, isLoading, error, success } = useUpdateNIS();
  const { iaList, loading: iaListLoading, error: iaListError } = useIAList();
  const { isList, loading: isListLoading, error: isListError } = useISList();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});


  
  // Initial form data
  const initialFormData = {
    is_name: '',
    years: '',
    ia_name: '',
    municipality: '',
    districtNum: '',
    mailing_address: '',
    ia_president: '',
    contactNum: '',
    dateOrganized: '',
    secNum: '',
    secDate: '',
    ia_tin: '',
    membership_potl: '',
    membership_actual: '',
    serviceArea: '',
    fusa: '',
    irrigated_area: '',
    operational_area: '',
    custom_is_name: '',
    custom_ia_name: '',
  };

  // Convert date to YYYY-MM-DD format
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Initialize form state
  const [formData, setFormData] = useState({
    is_name: selectedItem?.is_name || '',
    years: selectedItem?.years?.toString() || '',
    ia_name: selectedItem?.ia_name || '',
    municipality: selectedItem?.municipality || '',
    districtNum: selectedItem?.districtNum || '',
    mailing_address: selectedItem?.mailing_address || selectedItem?.mailingAddress || '', // Fallback to mailingAddress
    ia_president: selectedItem?.ia_president || '',
    contactNum: selectedItem?.contactNum || '',
    dateOrganized: convertDateFormat(selectedItem?.dateOrganized) || '',
    secNum: selectedItem?.secNum || '',
    secDate: convertDateFormat(selectedItem?.secDate) || '',
    ia_tin: selectedItem?.ia_tin || '',
    membership_potl: selectedItem?.membership_potl || '',
    membership_actual: selectedItem?.membership_actual || '',
    serviceArea: selectedItem?.serviceArea || '',
    fusa: selectedItem?.fusa || '',
    irrigated_area: selectedItem?.irrigated_area || '',
    operational_area: selectedItem?.operational_area || '',
    custom_is_name: '',
    custom_ia_name: '',
  });

  const formSections = [
    {
      title: null,
      fields: ['years'],
    },
    {
      title: null,
      fields: [
        'ia_name',
        'is_name',
        'municipality',
        'districtNum',
        'mailing_address',
        'ia_president',
        'contactNum',
        'dateOrganized',
      ],
    },
    {
      title: 'SEC/CDA/DOLE Reg.',
      fields: ['secNum', 'secDate'],
    },
    {
      title: null,
      fields: ['ia_tin'],
    },
    {
      title: 'Membership',
      fields: ['membership_potl', 'membership_actual'],
    },
    {
      title: null,
      fields: ['serviceArea', 'fusa', 'irrigated_area', 'operational_area'],
    },
  ];

  const fieldLabelMap = {
    is_name: 'IS Name',
    years: 'Year',
    ia_name: "Name of Irrigators' Association",
    municipality: 'Municipality',
    districtNum: 'District No.',
    mailing_address: 'Mailing Address',
    ia_president: 'Name of IA President',
    contactNum: 'Contact Number',
    dateOrganized: 'Date Organized',
    secNum: 'Number',
    secDate: 'Date',
    ia_tin: 'IA TIN',
    membership_potl: "POT'L",
    membership_actual: 'ACTUAL',
    serviceArea: 'Service Area',
    fusa: 'Fusa',
    irrigated_area: 'Irrigated Area',
    operational_area: 'Operational Area',
    custom_is_name: 'Custom IS Name',
    custom_ia_name: 'Custom IA Name',
  };

  // Generate year options (2000 to current year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

  // Log for debugging
  useEffect(() => {
    console.log('NISEdit received selectedItem:', selectedItem);
    console.log('formData:', formData);
    console.log('Problematic fields:', {
      years: formData.years,
      municipality: formData.municipality,
      mailing_address: formData.mailing_address,
    });
    console.log('Selected Municipality:', formData.municipality);
    console.log('Selected Mailing Address:', formData.mailing_address);
    console.log('iaList:', iaList);
    console.log('isList:', isList);
    if (!selectedItem || !selectedItem.id) {
      console.warn('Invalid selectedItem');
      setShowError(true);
    }
  }, [selectedItem, iaList, isList, formData]);
  

  
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((field) => {
      if (
        (field === 'custom_is_name' && formData.is_name !== 'Others ...') ||
        (field === 'custom_ia_name' && formData.ia_name !== 'Others ...')
      ) {
        return;
      }
  
      const value = formData[field];
  
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value !== 'string' && value === '') // covers numeric zeroes and non-strings
      ) {
        errors[field] = true;
      }
    });
  
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setShowError(true);
      console.log('Validation errors:', validationErrors);
      return;
    }
    const submissionData = {
      ...formData,
      is_name: formData.is_name === 'Others ...' ? formData.custom_is_name.toUpperCase() : formData.is_name,
      ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name.toUpperCase() : formData.ia_name,
    };
    try {
      await updateNIS(selectedItem.id, submissionData);
      setShowSuccess(true);
      setShowError(false);
      setTimeout(onClose, 1000);
    } catch (err) {
      setShowError(true);
      setShowSuccess(false);
      console.error('Update error:', err);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };
  

  useEffect(() => {
    console.log('selectedItem:', selectedItem);
    console.log('selectedItem.years:', selectedItem?.years);
    console.log('selectedItem.year:', selectedItem?.year);
  }, [selectedItem]);
  

  if (!selectedItem || !selectedItem.id) {
    return (
      <RightPanel darkMode={darkMode} show={true}>
        <FormContainer darkMode={darkMode}>
          <Title darkMode={darkMode}>
            <span>Edit NIS Record</span>
            <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
          </Title>
          <ErrorCard errorMessage="No valid record selected." onClose={handleClose} />
        </FormContainer>
      </RightPanel>
    );
  }

  

  return (
    <RightPanel darkMode={darkMode} show={true}>
      <FormContainer darkMode={darkMode}>
        <Title darkMode={darkMode}>
          <span>Edit NIS Record</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
        {(isLoading || iaListLoading || isListLoading) && (
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        )}
        {(error || iaListError || isListError) && (
          <ErrorMessage>{error || iaListError || isListError}</ErrorMessage>
        )}
        {showSuccess && (
          <SuccessCard
            message="NIS data updated successfully!"
            onClose={() => setShowSuccess(false)}
          />
        )}
        {showError && (
          <ErrorMessage>Please fill out all fields correctly.</ErrorMessage>
        )}
        <ErrorBoundary onClose={handleClose}>
          <StyledForm onSubmit={handleSubmit}>
            {formSections.map((section, index) => (
              <CardSection darkMode={darkMode} key={index}>
                {section.title && (
                  <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
                )}
                {section.fields.map((key) => (
                  <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                    <Label htmlFor={key} hasError={validationErrors[key]}>
                      {fieldLabelMap[key]}
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </Label>
                    {key === 'years' ? (
  (() => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

    const options = yearOptions.includes(formData.years)
      ? yearOptions
      : [formData.years, ...yearOptions];

    return (
      <Select
        id={key}
        name={key}
        value={formData[key]}
        onChange={handleChange}
        aria-label={fieldLabelMap[key]}
        darkMode={darkMode}
        hasError={validationErrors[key]}
      >
        <option value="">Select Year</option>
        {options.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </Select>
    );
  })()
) : key === 'is_name' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          darkMode={darkMode}
                          hasError={validationErrors[key]}
                        >
                          <option value="">Select IS Name</option>
                          {isList?.length > 0 ? (
                            isList.map((is) => (
                              <option key={is.is_name} value={is.is_name}>
                                {is.is_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No IS names available
                            </option>
                          )}
                          <option value="Others ...">Add new IS</option>
                        </Select>
                        {formData.is_name === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_is_name"
                            name="custom_is_name"
                            value={formData.custom_is_name}
                            onChange={handleChange}
                            placeholder="Enter Custom IS Name"
                            aria-label={fieldLabelMap.custom_is_name}
                            darkMode={darkMode}
                            hasError={validationErrors.custom_is_name}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : key === 'ia_name' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          darkMode={darkMode}
                          hasError={validationErrors[key]}
                        >
                          <option value="">Select IA Name</option>
                          {iaList?.length > 0 ? (
                            iaList.map((ia) => (
                              <option key={ia.ia_name} value={ia.ia_name}>
                                {ia.ia_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No IA names available
                            </option>
                          )}
                          <option value="Others ...">Add new IA</option>
                        </Select>
                        {formData.ia_name === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_ia_name"
                            name="custom_ia_name"
                            value={formData.custom_ia_name}
                            onChange={handleChange}
                            placeholder="Enter Custom IA Name"
                            aria-label={fieldLabelMap.custom_ia_name}
                            darkMode={darkMode}
                            hasError={validationErrors.custom_ia_name}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : key === 'municipality' ? (
                      <MunicipalitySelector
  formData={formData}
  setFormData={setFormData}
  darkMode={darkMode}
  hasError={validationErrors[key]}
/>
) : key === 'mailing_address' ? (
  <MailingAddressSelector
  formData={formData}
  setFormData={setFormData}
  darkMode={darkMode}
  hasError={validationErrors[key]}
/>
) :  key === 'dateOrganized' || key === 'secDate' ? (
                      <Input
                        type="date"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : key === 'districtNum' ? (
                      <Input
                        type="text"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={fieldLabelMap[key]}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : key === 'membership_potl' ||
                      key === 'membership_actual' ||
                      key === 'serviceArea' ||
                      key === 'fusa' ||
                      key === 'irrigated_area' ||
                      key === 'operational_area' ? (
                      <Input
                        type="number"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={fieldLabelMap[key]}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : (
                      <Input
                        type="text"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={
                          key === 'contactNum'
                            ? 'Enter Contact Number (7-15 digits)'
                            : key === 'ia_tin'
                            ? 'Enter TIN (alphanumeric, up to 20 chars)'
                            : fieldLabelMap[key]
                        }
                        aria-label={fieldLabelMap[key]}
                        maxLength={
                          key === 'contactNum' ? '15' : key === 'ia_tin' ? '20' : '100'
                        }
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    )}
                  </FieldGroup>
                ))}
              </CardSection>
            ))}
            <FormButtonContainer>
              <SubmitButton type="submit" darkMode={darkMode} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Information'}
              </SubmitButton>
            </FormButtonContainer>
          </StyledForm>
        </ErrorBoundary>
      </FormContainer>
    </RightPanel>
  );
};

export default NISEdit;


// Styled Components
const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 45%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 5;
  padding: 20px;
  transition: right 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-radius: 8px;
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  span {
    text-align: center;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
`;